/**
 * Zustand store for map UI state.
 *
 * Keeps ephemeral client-side state out of React Query:
 *  - Date range slider position
 *  - Current map viewport bounds
 *  - Active region / selected pin
 *  - Sidebar open/close
 *  - "Fog of War" rate-limit warning
 */
import { create } from 'zustand';
import { DEFAULT_DATE_RANGE } from '@/lib/constants';

export const useMapStore = create((set, get) => ({
  // ── Date range (temporal slider) ──────────────────────────────────────────
  dateRange: { ...DEFAULT_DATE_RANGE },
  setDateRange: (dateRange) => set({ dateRange }),

  // ── Playback mode ─────────────────────────────────────────────────────────
  isPlaying: false,
  setIsPlaying: (isPlaying) => set({ isPlaying }),

  // ── Map viewport ──────────────────────────────────────────────────────────
  // Bounding box as [west, south, east, north]
  mapBounds: null,
  setMapBounds: (mapBounds) => set({ mapBounds }),

  // Current zoom level
  mapZoom: 2.5,
  setMapZoom: (mapZoom) => set({ mapZoom }),

  // ── Selected feature ──────────────────────────────────────────────────────
  selectedFeature: null,   // GeoJSON Feature object
  setSelectedFeature: (selectedFeature) => set({ selectedFeature, isSidebarOpen: !!selectedFeature }),
  clearSelectedFeature: () => set({ selectedFeature: null }),

  // ── Story sidebar ─────────────────────────────────────────────────────────
  isSidebarOpen: false,
  setSidebarOpen: (isSidebarOpen) => set({ isSidebarOpen }),
  toggleSidebar: () => set((s) => ({ isSidebarOpen: !s.isSidebarOpen })),

  // ── Give Now modal ────────────────────────────────────────────────────────
  isGiveModalOpen: false,
  giveContext: null,        // { regionName?, catalystId? } — context for the donation
  openGiveModal: (giveContext = null) => set({ isGiveModalOpen: true, giveContext }),
  closeGiveModal: () => set({ isGiveModalOpen: false, giveContext: null }),

  // ── Anti-scraping / Fog of War ────────────────────────────────────────────
  fogOfWarActive: false,
  fogOfWarCountdown: 0,
  triggerFogOfWar: (seconds = 10) => {
    set({ fogOfWarActive: true, fogOfWarCountdown: seconds });
    const interval = setInterval(() => {
      const { fogOfWarCountdown } = get();
      if (fogOfWarCountdown <= 1) {
        clearInterval(interval);
        set({ fogOfWarActive: false, fogOfWarCountdown: 0 });
      } else {
        set((s) => ({ fogOfWarCountdown: s.fogOfWarCountdown - 1 }));
      }
    }, 1000);
  },

  // ── Region search ─────────────────────────────────────────────────────────
  searchQuery: '',
  setSearchQuery: (searchQuery) => set({ searchQuery }),

  // ── Reset (called on logout) ──────────────────────────────────────────────
  reset: () => set({
    dateRange:        { ...DEFAULT_DATE_RANGE },
    isPlaying:        false,
    mapBounds:        null,
    mapZoom:          2.5,
    selectedFeature:  null,
    isSidebarOpen:    false,
    isGiveModalOpen:  false,
    giveContext:      null,
    fogOfWarActive:   false,
    fogOfWarCountdown: 0,
    searchQuery:      '',
  }),
}));
